import { useQuery } from "@tanstack/react-query";
import { getStoryMeta } from "../services/dataStories/storiesApi";
import { getApiErrorMessage, retryUnlessNotFound } from "../services/apiErrors";
import { queryKeys } from "../services/queryKeys";
import type { StoryMetaApi } from "../services/dataStories/storiesApi.types";

const LOAD_ERROR_FALLBACK = "Unable to load the story.";
const MAX_RETRIES = 1;
const POLL_INTERVAL_MS = 5_000;

/**
 * Above zero on purpose, against the app-wide default of 0 (see
 * QueryProvider.tsx). `StoryLayout` mounts this hook first, gates its
 * `<Outlet>` on the load finishing, and `useStoryBuildFlow` (inside the
 * mounted page) mounts it again for the same key a render later. At
 * staleTime 0 that second mount always re-fetched, even though the first
 * fetch had just landed — the story GET fired twice on every page open. Every
 * real change to this row already goes through `invalidateQueries` or
 * `setQueryData` (rename, publish, auto-refresh, chat send, build/refresh
 * completion), which ignore staleTime, so this only suppresses the redundant
 * re-fetch, not a real update.
 */
const STORY_STALE_TIME_MS = 15_000;

export interface UseStoryMetaOptions {
  /**
   * Refetch every 5s until `status` is terminal (`ready`/`failed`). The build
   * page turns it on to wait for the pipeline after a socket failure, a chat
   * regeneration or a refresh. The request carries `?poll=true` so the
   * backend skips the auto-refresh side effect of a plain story open.
   */
  poll?: boolean;
}

export interface UseStoryMetaResult {
  storyMeta: StoryMetaApi | null;
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
}

/**
 * The story row: title, status, publish status, auto-refresh flag. One cache
 * entry (`queryKeys.storyMeta.detail`) shared by StoryLayout's outlet context,
 * the build flow's status gate, the header pill and the auto-refresh toggle,
 * so a rename or publish shows everywhere without a refetch.
 */
export const useStoryMeta = (
  workspaceId: string | undefined,
  storyId: string | undefined,
  { poll = false }: UseStoryMetaOptions = {},
): UseStoryMetaResult => {
  const query = useQuery<StoryMetaApi, Error>({
    queryKey: queryKeys.storyMeta.detail(workspaceId ?? "", storyId ?? ""),
    queryFn: () => getStoryMeta(workspaceId!, storyId!, poll),
    enabled: !!workspaceId && !!storyId,
    // A 404 is a story that isn't in this workspace.
    retry: retryUnlessNotFound(MAX_RETRIES),
    staleTime: STORY_STALE_TIME_MS,
    refetchInterval: poll
      ? (current) => {
          const status = current.state.data?.status;
          // Stop once the pipeline reaches a terminal state.
          if (status === "ready" || status === "failed") return false;
          return POLL_INTERVAL_MS;
        }
      : false,
    refetchIntervalInBackground: poll,
  });

  return {
    storyMeta: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    errorMessage: query.isError ? getApiErrorMessage(query.error, LOAD_ERROR_FALLBACK) : null,
  };
};
